import { Link } from '@atoms'
import EmptyState, { type EmptyStateProps } from '@components/molecules/EmptyState'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<EmptyStateProps> = {
  iconType: { type: 'IconTypes', required: true, description: 'Sprite glyph shown in the circle.' },
  headline: { type: 'string', required: true, description: 'Says what is missing, like "No call lists yet".' },
  subtext: { type: 'string', description: 'Says what to do next.' },
  className: {
    type: 'string',
    description: 'Merged with cn. Pass tw:border-0 when it sits inside a frame that already has a border.',
  },
  children: { type: 'ReactNode', description: 'One or two actions, centred below the text.' },
}

const doc: ComponentDoc<EmptyStateProps> = {
  slug: 'empty-state',
  name: 'EmptyState',
  tier: 'molecule',
  description:
    'Fills the space a list or table would take when it has nothing to show. It has the same border as a DataTable.',
  importStatement: "import EmptyState from '@components/molecules/EmptyState'",
  sourcePath: 'app/frontend/components/molecules/EmptyState.tsx',
  component: EmptyState,
  props,
  examples: [
    {
      title: 'Basic',
      render: () => (
        <EmptyState
          iconType="phone-call-01"
          headline="No calling sessions yet"
          subtext="Create your first calling session to start contacting supporters."
        />
      ),
      code: `<EmptyState
  iconType="phone-call-01"
  headline="No calling sessions yet"
  subtext="Create your first calling session to start contacting supporters."
/>`,
    },
    {
      title: 'With an action',
      render: () => (
        <EmptyState iconType="calendar-date" headline="No events yet" subtext="Add an event to start building rosters.">
          <Link href="#" rank="primary" trailingIcon="plus-circle">
            Add event
          </Link>
        </EmptyState>
      ),
      code: `<EmptyState iconType="calendar-date" headline="No events yet" subtext="Add an event to start building rosters.">
  <Link href={newEventPath} rank="primary" trailingIcon="plus-circle">
    Add event
  </Link>
</EmptyState>`,
    },
    {
      title: 'With a way out of a filter',
      render: () => (
        <EmptyState iconType="phone-call-01" headline="No call lists in Brisbane Friends">
          <Link href="#" rank="primary" trailingIcon="plus-circle">
            Add call list
          </Link>
          <Link href="#" rank="secondary">
            Show all groups
          </Link>
        </EmptyState>
      ),
      code: `<EmptyState iconType="phone-call-01" headline="No call lists in Brisbane Friends">
  <Link href={newCallListPath} rank="primary" trailingIcon="plus-circle">
    Add call list
  </Link>
  <Link as="inertiaLink" href={callListsPath} rank="secondary">
    Show all groups
  </Link>
</EmptyState>`,
    },
  ],
  usage: {
    do: [
      'Render it in place of the list or table.',
      'Give a no-results state a button that clears the search or filters.',
      'Offer the next step as a child, like a link to create the first item.',
    ],
    dont: ['Wrap it in another bordered box. It brings its own border.', 'Use it for an error or a loading state.'],
  },
  a11y: [
    'The headline is an h3. Check it fits the heading order of the page.',
    'The icon is decorative. The headline and subtext carry the meaning.',
    'Wrap it in role="status" when it appears after a search, so screen readers announce it. Skip that when the page already has a status region above it that announces the result, as the search result Alerts on the group pages do.',
  ],
}

export default doc
